import React, { useState, useEffect } from 'react';
import useNonExchangeDayService from '../../../../app/services/hooks/useNonExchangeDayService';
import useForeingExchangeService from '../../../../app/services/hooks/useForeignExchangeService';
import { ServiceResponse } from '../../../../app/services/shared/interfaces';
import { NonExchangeDay } from '../../../../app/models/NonExchangeDay';
import { ForeignExchanges } from '../../../../app/models/ForeignExchange';
import { formatAmount } from '../../../../utils/utils';

interface Props {
    currencyId: number;
}

interface CalendarDay {
    date: Date;
    isCurrentMonth: boolean;
    isSelected: boolean;
    nonExchangeDay?: NonExchangeDay;
    foreignExchange?: ForeignExchanges;
}

type PeriodType = 'month' | 'year';
type ViewType = 'calendar' | 'table';

const CurrencyCalendar: React.FC<Props> = ({ currencyId }) => {
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [nonExchangeDays, setNonExchangeDays] = useState<NonExchangeDay[]>([]);
    const [foreignExchanges, setForeignExchanges] = useState<ForeignExchanges[]>([]);
    const [periodType, setPeriodType] = useState<PeriodType>('month');
    const [viewType, setViewType] = useState<ViewType>('calendar');
    const { getNonExchangeDaysByDate, fetchinggetNonExchangeDaysByDate } = useNonExchangeDayService();
    const { getForeignExchangesByDate, fetchingGetForeignExchangesByDate } = useForeingExchangeService();

    useEffect(() => {
        loadNonExchangeDays();
        loadForeignExchanges();
    }, [currentDate, currencyId, periodType, viewType]);

    const loadNonExchangeDays = () => {
        getNonExchangeDaysByDate(
            currencyId,
            currentDate.getFullYear(),
            periodType === 'month' ? currentDate.getMonth() + 1 : undefined,
            {
                onSuccess: (response: ServiceResponse) => {
                    setNonExchangeDays(response.data.non_exchange_days);
                }
            }
        );
    };

    const loadForeignExchanges = () => {
        getForeignExchangesByDate(
            currencyId,
            currentDate.getFullYear(),
            periodType === 'month' ? currentDate.getMonth() + 1 : undefined,
            {
                onSuccess: (response: ServiceResponse) => {
                    setForeignExchanges(response.data.foreign_exchanges);
                }
            }
        );
    };

    const getDaysInMonth = (date: Date): CalendarDay[] => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDay = firstDay.getDay();

        const days: CalendarDay[] = [];

        // Agregar días del mes anterior
        const prevMonth = month - 1;
        const prevMonthYear = prevMonth < 0 ? year - 1 : year;
        const prevMonthIndex = prevMonth < 0 ? 11 : prevMonth;
        const prevMonthLastDay = new Date(prevMonthYear, prevMonthIndex + 1, 0).getDate();

        for (let i = 0; i < startingDay; i++) {
            const prevDate = new Date(prevMonthYear, prevMonthIndex, prevMonthLastDay - startingDay + i + 1);
            days.push({
                date: prevDate,
                isCurrentMonth: false,
                isSelected: false
            });
        }

        // Agregar días del mes actual
        for (let i = 1; i <= daysInMonth; i++) {
            const currentDate = new Date(year, month, i);
            const nonExchangeDay = nonExchangeDays.find(day => {
                const dayDate = new Date(day.date);
                const currentDateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
                return day.date === currentDateStr;
            });

            const foreignExchange = foreignExchanges.find(exchange => {
                if (!exchange.exchange_date) return false;
                const exchangeDate = new Date(exchange.exchange_date);
                const currentDateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
                return exchange.exchange_date === currentDateStr;
            });

            days.push({
                date: currentDate,
                isCurrentMonth: true,
                isSelected: selectedDate ? 
                    currentDate.toDateString() === selectedDate.toDateString() : 
                    false,
                nonExchangeDay,
                foreignExchange
            });
        }

        // Agregar días del mes siguiente
        const nextMonth = month + 1;
        const nextMonthYear = nextMonth > 11 ? year + 1 : year;
        const nextMonthIndex = nextMonth > 11 ? 0 : nextMonth;
        const remainingDays = 42 - days.length; // 6 semanas * 7 días = 42

        for (let i = 1; i <= remainingDays; i++) {
            const nextDate = new Date(nextMonthYear, nextMonthIndex, i);
            days.push({
                date: nextDate,
                isCurrentMonth: false,
                isSelected: false
            });
        }

        return days;
    };

    const getMonthsInYear = () => {
        const year = currentDate.getFullYear();
        const months = [];
        
        for (let month = 0; month < 12; month++) {
            const date = new Date(year, month, 1);
            const daysInMonth = new Date(year, month + 1, 0).getDate();
            const firstDay = new Date(year, month, 1).getDay();
            
            months.push({
                month,
                date,
                daysInMonth,
                firstDay,
                days: getDaysInMonth(date)
            });
        }
        
        return months;
    };

    const handlePrevMonth = () => {
        if (periodType === 'month') {
            setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
        } else {
            setCurrentDate(new Date(currentDate.getFullYear() - 1, 0, 1));
        }
    };

    const handleNextMonth = () => {
        if (periodType === 'month') {
            setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
        } else {
            setCurrentDate(new Date(currentDate.getFullYear() + 1, 0, 1));
        }
    };

    const handleDateClick = (date: Date) => {
        setSelectedDate(date);
    };

    const handlePeriodChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setPeriodType(event.target.value as PeriodType);
    };

    const handleViewTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setViewType(event.target.value as ViewType);
    };

    const renderMonthlyTableView = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

        return (
            <div className="table-responsive">
                <table className="table table-bordered">
                    <thead>
                        <tr>
                            <th>Día</th>
                            <th>Valor / Día sin Cambio</th>
                        </tr>
                    </thead>
                    <tbody>
                        {days.map(day => {
                            const currentDateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                            const nonExchangeDay = nonExchangeDays.find(d => d.date === currentDateStr);
                            const foreignExchange = foreignExchanges.find(exchange => exchange.exchange_date === currentDateStr);

                            return (
                                <tr key={day}>
                                    <td>{day}</td>
                                    <td className={nonExchangeDay ? 'non-exchange' : ''}>
                                        {nonExchangeDay && (
                                            <div className="non-exchange-text">
                                                {nonExchangeDay.name}
                                            </div>
                                        )}
                                        {foreignExchange?.value && (
                                            <div>
                                                {formatAmount(foreignExchange.value)} CLP
                                            </div>
                                        )}
                                        {!nonExchangeDay && !foreignExchange?.value && '-'}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    const renderAnnualTableView = () => {
        const year = currentDate.getFullYear();
        const daysInYear = new Date(year, 11, 31).getDate();
        const months = Array.from({ length: 12 }, (_, i) => i);
        const days = Array.from({ length: daysInYear }, (_, i) => i + 1);

        return (
            <div className="table-responsive">
                <table className="table table-bordered">
                    <thead>
                        <tr>
                            <th>Día</th>
                            {months.map(month => (
                                <th key={month}>{monthNames[month]}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {days.map(day => (
                            <tr key={day}>
                                <td>{day}</td>
                                {months.map(month => {
                                    const currentDateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                                    const nonExchangeDay = nonExchangeDays.find(d => d.date === currentDateStr);
                                    const foreignExchange = foreignExchanges.find(exchange => exchange.exchange_date === currentDateStr);

                                    return (
                                        <td 
                                            key={month}
                                            className={nonExchangeDay ? 'non-exchange' : ''}
                                            title={nonExchangeDay ? nonExchangeDay.name : ''}
                                        >
                                            {nonExchangeDay && (
                                                <div className="non-exchange-text">
                                                    {nonExchangeDay.name}
                                                </div>
                                            )}
                                            {foreignExchange?.value && (
                                                <div className="exchange-value">
                                                    {formatAmount(foreignExchange.value)}
                                                </div>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    const weekDays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
    const monthNames = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];

    const renderMonthView = () => {
        const days = getDaysInMonth(currentDate);
        return (
            <div className="calendar-grid">
                <div className="calendar-weekdays">
                    {weekDays.map(day => (
                        <div key={day} className="calendar-weekday">
                            {day}
                        </div>
                    ))}
                </div>
                <div className="calendar-days">
                    {days.map((day, index) => (
                        <div
                            key={index}
                            className={`calendar-day ${
                                !day.isCurrentMonth ? 'other-month' : ''
                            } ${
                                day.isSelected ? 'selected' : ''
                            } ${
                                day.nonExchangeDay ? 'non-exchange' : ''
                            }`}
                            onClick={() => handleDateClick(day.date)}
                            title={day.nonExchangeDay ? day.nonExchangeDay.name : ''}
                        >
                            <span className="day-number">{day.date.getDate()}</span>
                            {day.nonExchangeDay && (
                                <div className="exchange-value non-exchange-text">
                                    {day.nonExchangeDay.name}
                                </div>
                            )}
                            {day.foreignExchange?.value && (
                                <div className="exchange-value">
                                    {formatAmount(day.foreignExchange.value)} CLP
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderYearView = () => {
        const months = getMonthsInYear();
        return (
            <div className="calendar-year-grid">
                {months.map((monthData, monthIndex) => (
                    <div key={monthIndex} className="calendar-month">
                        <h4 className="month-title">{monthNames[monthData.month]}</h4>
                        <div className="calendar-grid">
                            <div className="calendar-weekdays">
                                {weekDays.map(day => (
                                    <div key={day} className="calendar-weekday">
                                        {day}
                                    </div>
                                ))}
                            </div>
                            <div className="calendar-days">
                                {monthData.days.map((day, index) => (
                                    <div
                                        key={index}
                                        className={`calendar-day ${
                                            !day.isCurrentMonth ? 'other-month' : ''
                                        } ${
                                            day.isSelected ? 'selected' : ''
                                        } ${
                                            day.nonExchangeDay ? 'non-exchange' : ''
                                        }`}
                                        onClick={() => handleDateClick(day.date)}
                                        title={day.nonExchangeDay ? day.nonExchangeDay.name : ''}
                                    >
                                        <span className="day-number">{day.date.getDate()}</span>
                                        {day.nonExchangeDay && (
                                            <div className="exchange-value non-exchange-text">
                                                {day.nonExchangeDay.name}
                                            </div>
                                        )}
                                        {day.foreignExchange?.value && (
                                            <div className="exchange-value">
                                                {formatAmount(day.foreignExchange.value)} CLP
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    return (
        <div className="calendar-container">
            <div className="calendar-header row justify-content-between align-items-center mb-4">
                <div className="col-auto d-flex align-items-center">
                    <button 
                        className="btn btn-outline-primary me-2" 
                        onClick={handlePrevMonth}
                    >
                        <i className="mdi mdi-chevron-left"></i>
                    </button>
                    <h3 className="mb-0">
                        {periodType === 'month' 
                            ? `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`
                            : currentDate.getFullYear()
                        }
                    </h3>
                    <button 
                        className="btn btn-outline-primary ms-2" 
                        onClick={handleNextMonth}
                    >
                        <i className="mdi mdi-chevron-right"></i>
                    </button>
                </div>
                <div className="col-auto">
                    <div className="d-none d-sm-flex align-items-center gap-2">
                        <select 
                            className="form-select form-select-sm"
                            value={periodType}
                            onChange={handlePeriodChange}
                        >
                            <option value="month">Vista Mensual</option>
                            <option value="year">Vista Anual</option>
                        </select>
                        <select 
                            className="form-select form-select-sm"
                            value={viewType}
                            onChange={handleViewTypeChange}
                        >
                            <option value="calendar">Vista Calendario</option>
                            <option value="table">Vista Tabla</option>
                        </select>
                    </div>
                    <div className="d-flex d-sm-none flex-column gap-2">
                        <select 
                            className="form-select form-select-sm"
                            value={periodType}
                            onChange={handlePeriodChange}
                        >
                            <option value="month">Vista Mensual</option>
                            <option value="year">Vista Anual</option>
                        </select>
                        <select 
                            className="form-select form-select-sm"
                            value={viewType}
                            onChange={handleViewTypeChange}
                        >
                            <option value="calendar">Vista Calendario</option>
                            <option value="table">Vista Tabla</option>
                        </select>
                    </div>
                </div>
            </div>

            {viewType === 'table' ? 
                (periodType === 'month' ? renderMonthlyTableView() : renderAnnualTableView()) :
                (periodType === 'month' ? renderMonthView() : renderYearView())
            }

            <style>{`
                .calendar-grid {
                    border: 1px solid #e0e0e0;
                    border-radius: 4px;
                    width: 100%;
                }

                .calendar-weekdays {
                    display: grid;
                    grid-template-columns: repeat(7, 1fr);
                    background: #f8f9fa;
                    border-bottom: 1px solid #e0e0e0;
                }

                .calendar-weekday {
                    text-align: center;
                    padding: 8px 4px;
                    font-weight: 500;
                    color: #495057;
                    font-size: 0.9rem;
                }

                .calendar-days {
                    display: grid;
                    grid-template-columns: repeat(7, 1fr);
                }

                .calendar-day {
                    min-height: 80px;
                    padding: 4px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    position: relative;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    border-right: 1px solid #e0e0e0;
                    border-bottom: 1px solid #e0e0e0;
                    background: white;
                }

                .calendar-day:nth-child(7n) {
                    border-right: none;
                }

                .calendar-day:hover {
                    background: #f8f9fa;
                }

                .calendar-day.other-month {
                    background: #f8f9fa;
                    color: #adb5bd;
                }

                .calendar-day.selected {
                    background: #e3f2fd;
                }

                .calendar-day.non-exchange {
                    background: #ffebee;
                }

                .calendar-day.non-exchange:hover {
                    background: #ffcdd2;
                }

                .day-number {
                    font-size: 14px;
                    font-weight: 500;
                    margin-bottom: 2px;
                }

                .exchange-value {
                    font-size: 11px;
                    color: #2196f3;
                    text-align: center;
                    line-height: 1.1;
                    max-width: 100%;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .exchange-value.non-exchange-text {
                    color: #d32f2f;
                    font-size: 10px;
                    font-weight: 500;
                    margin-bottom: 2px;
                }

                .table td .exchange-value {
                    font-size: 12px;
                    margin-top: 4px;
                }

                .calendar-year-grid {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    gap: 20px;
                }

                .calendar-month {
                    background: #f8f9fa;
                    border-radius: 8px;
                    padding: 15px;
                }

                .month-title {
                    text-align: center;
                    margin-bottom: 15px;
                    color: #495057;
                    font-size: 1.1rem;
                }

                @media (max-width: 1200px) {
                    .calendar-year-grid {
                        grid-template-columns: repeat(2, 1fr);
                    }
                }

                @media (max-width: 768px) {
                    .calendar-year-grid {
                        grid-template-columns: 1fr;
                    }

                    .calendar-weekday {
                        padding: 4px 2px;
                        font-size: 0.75rem;
                    }

                    .day-number {
                        font-size: 12px;
                    }

                    .exchange-value {
                        font-size: 9px;
                    }

                    .exchange-value.non-exchange-text {
                        font-size: 8px;
                    }

                    .month-title {
                        font-size: 1rem;
                        margin-bottom: 8px;
                    }

                    .calendar-month {
                        padding: 8px;
                    }

                    .calendar-grid {
                        border-radius: 2px;
                    }

                    .calendar-days {
                        gap: 0;
                    }

                    .calendar-day {
                        padding: 2px 1px;
                    }
                }

                @media (max-width: 480px) {
                    .calendar-weekday {
                        font-size: 0.7rem;
                        padding: 2px 1px;
                    }

                    .day-number {
                        font-size: 11px;
                        margin-bottom: 1px;
                    }

                    .exchange-value {
                        font-size: 8px;
                    }

                    .exchange-value.non-exchange-text {
                        font-size: 7px;
                    }

                    .calendar-day {
                        padding: 1px;
                    }
                }

                .gap-2 {
                    gap: 0.5rem;
                }

                .table {
                    margin-bottom: 0;
                }

                .table th {
                    background: #EFF2F7;
                    color: #495057;
                    font-weight: 600;
                    position: sticky;
                    top: 0;
                    z-index: 1;
                    white-space: nowrap;
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 2px solid #dde2e9;
                }

                .table td {
                    text-align: center;
                    min-width: 40px;
                    padding: 0.75rem;
                    vertical-align: middle;
                }

                .table-bordered > :not(caption) > * > * {
                    border-width: 1px;
                    border-color: #dde2e9;
                }

                .table td.non-exchange {
                    background: #ffebee;
                }

                .table td.non-exchange:hover {
                    background: #ffcdd2;
                }

                .table-responsive {
                    border-radius: 0.25rem;
                    box-shadow: 0 0 0.5rem rgba(0,0,0,0.08);
                    border: 1px solid #dde2e9;
                }

                .form-select {
                    min-width: 150px;
                }

                @media (max-width: 576px) {
                    .calendar-header {
                        flex-direction: column;
                        gap: 1rem;
                    }

                    .calendar-header > div {
                        width: 100%;
                        justify-content: center;
                    }
                }

                .table td .non-exchange-text {
                    color: #d32f2f;
                    font-size: 12px;
                    margin-bottom: 4px;
                }
            `}</style>
        </div>
    );
};

export default CurrencyCalendar; 